/*
 * `?return=<origin>` on /create names the Direwolf AI app someone came from, so that after sign-up the
 * account site can send them back there. Only origins on the build's allow-list (VITE_RETURN_ORIGINS)
 * count, only https, and only the origin itself: a path, query, fragment or user info makes the value
 * invalid, and nothing but the origin is ever used.
 */

/** `value` normalized (`https://host[:port]`) when it is exactly an https origin, else null. */
export function httpsOrigin(value: string): string | null {
  const text = value.trim();
  if (text === '') return null;
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || url.username !== '' || url.password !== '') return null;
  // A DNS name (international names arrive punycoded); no wildcards or other oddities.
  if (!/^[a-z0-9-]+(?:\.[a-z0-9-]+)+$/.test(url.hostname)) return null;
  // `https://app.example` and `https://app.example/` are the origin; anything after the host is not.
  if (url.pathname !== '/' || url.search !== '' || url.hash !== '' || /[?#]/.test(text))
    return null;
  return url.origin;
}

/** The allow-list from VITE_RETURN_ORIGINS (comma-separated); entries that are not https origins are ignored. */
export function parseReturnOrigins(raw: string | undefined): ReadonlySet<string> {
  const origins = new Set<string>();
  for (const entry of (raw ?? '').split(',')) {
    const origin = httpsOrigin(entry);
    if (origin !== null) origins.add(origin);
  }
  return origins;
}

/** The allow-listed origin that `value` names, or null. */
export function allowedReturnOrigin(value: unknown, allowed: ReadonlySet<string>): string | null {
  if (typeof value !== 'string') return null;
  const origin = httpsOrigin(value);
  return origin !== null && allowed.has(origin) ? origin : null;
}
